import React from 'react';
import { Descriptions, Grid, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

const { useBreakpoint } = Grid;
const { Paragraph } = Typography;

const PatientSummary = ({ patient }) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();

  // Determine current column count based on breakpoints
  let columns = 1;
  if (screens.xl || screens.lg) columns = 4;
  else if (screens.md || screens.sm) columns = 2;

  const renderField = (key, label, value, alwaysShow = false) => {
    // Check if value is empty (null, undefined, empty string, or 0)
    const isEmpty = value === null || value === undefined || value === '' || value === 0;
    if (!alwaysShow && isEmpty) return null;

    // If value is a React element, render it directly
    if (React.isValidElement(value)) {
      return (
        <Descriptions.Item key={key} label={label}>
          {value}
        </Descriptions.Item>
      );
    }

    // Convert value to string for display
    const displayValue = value?.toString() || '';
    
    return (
      <Descriptions.Item key={key} label={label}>
        {displayValue}
      </Descriptions.Item>
    );
  };

  const comments = patient.getComments();
  const hasComments = comments && comments.trim() !== '';

  return (
    <>
      <Descriptions
        size="small"
        column={{ xs: 1, sm: 2, md: 2, lg: 4, xl: 4 }}
        contentStyle={{ wordBreak: 'break-word' }}
      >
        {renderField('gender', t('patient_form.fields.gender'), patient.getGender(), true)}
        {renderField('age', t('patient_form.fields.age'), patient.getDOBFormatted(), true)}
        {renderField('location', t('patient_form.fields.location_prefix'), patient.getLocation())}
        {renderField('civil_status', t('patient_form.fields.civil_status'), patient.getCivilStatus())}
        {renderField('occupation', t('patient_form.fields.occupation'), patient.getOccupation())}
        {renderField('religion', t('patient_form.fields.religion'), patient.getReligion())}
        {renderField('id', t('patient_form.fields.id'), patient.getExternalId())}
        {renderField('mobile', t('patient_form.fields.mobile_number'), patient.getMobileNumber())}
        {renderField('email', t('patient_form.fields.email'), patient.getEmail())}
        {renderField('weight', t('patient_form.fields.weight'), patient.getWeightDisplay())}
        {renderField('height', t('patient_form.fields.height'), patient.getHeightDisplay())}
        {renderField('bmi', t('patient_form.fields.bmi'), patient.getCalculatedBMI())}
        {renderField('blood_type', t('patient_form.fields.blood_type'), patient.getBloodType())}
        {renderField('link', t('patient_form.fields.link_short'), patient.getLinkElement())}
      </Descriptions>
      {hasComments && (
        <Descriptions
          size="small"
          column={1}
          contentStyle={{ wordBreak: 'break-word' }}
        >
          <Descriptions.Item label={t('patient_form.fields.comments')}>
            <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-line' }}>
              {comments}
            </Paragraph>
          </Descriptions.Item>
        </Descriptions>
      )}
    </>
  );
};

export default PatientSummary;